import React from 'react';
import { Box, IconButton, Typography, Button } from '@mui/material';
import FirstPageIcon from '@mui/icons-material/FirstPage';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import LastPageIcon from '@mui/icons-material/LastPage';

const Pagination = ({ 
    currentPage, 
    totalPages, 
    onPageChange, 
    totalItems, 
    itemsPerPage,
    startIndex,
    endIndex 
}) => {
    const handleFirstPage = () => {
        if (currentPage > 1) {
            onPageChange(1);
        }
    };

    const handlePreviousPage = () => {
        if (currentPage > 1) {
            onPageChange(currentPage - 1);
        }
    };

    const handleNextPage = () => {
        if (currentPage < totalPages) {
            onPageChange(currentPage + 1);
        }
    };

    const handleLastPage = () => {
        if (currentPage < totalPages) {
            onPageChange(totalPages);
        }
    };

    const renderPageNumbers = () => {
        const pages = [];
        const maxVisiblePages = 5;
        
        let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
        let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
        
        if (endPage - startPage + 1 < maxVisiblePages) {
            startPage = Math.max(1, endPage - maxVisiblePages + 1);
        }

        for (let i = startPage; i <= endPage; i++) {
            pages.push(
                <Button
                    key={i}
                    onClick={() => onPageChange(i)}
                    variant={currentPage === i ? "contained" : "outlined"}
                    sx={{
                        mx: 0.5,
                        minWidth: '40px',
                        height: '40px',
                        borderRadius: '20px',
                        backgroundColor: currentPage === i ? 'linear-gradient(45deg, #667eea, #764ba2)' : 'transparent',
                        color: currentPage === i ? 'white' : '#667eea',
                        border: currentPage === i ? 'none' : '2px solid #667eea',
                        fontWeight: 600,
                        '&:hover': {
                            backgroundColor: currentPage === i ? 'linear-gradient(45deg, #5a6fd8, #6a4190)' : 'rgba(102, 126, 234, 0.1)',
                            transform: 'translateY(-2px)',
                            boxShadow: currentPage === i ? '0 8px 25px rgba(102, 126, 234, 0.6)' : '0 5px 15px rgba(102, 126, 234, 0.3)',
                        },
                        transition: 'all 0.3s ease'
                    }}
                >
                    {i}
                </Button>
            );
        }
        
        return pages;
    };

    if (totalPages <= 1) {
        return null;
    }

    return (
        <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            mt: 3,
            p: 3,
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95), rgba(255, 255, 255, 0.9))',
            backdropFilter: 'blur(10px)',
            borderRadius: '20px',
            boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
            border: '1px solid rgba(255, 255, 255, 0.2)'
        }}>
            <Typography 
                variant="body2" 
                sx={{ 
                    color: '#666',
                    fontWeight: 500,
                    background: 'linear-gradient(45deg, #667eea, #764ba2)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent'
                }}
            >
                Mostrando {startIndex + 1} a {endIndex} de {totalItems} registros
            </Typography>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <IconButton
                    onClick={handleFirstPage}
                    disabled={currentPage === 1}
                    size="small"
                    sx={{
                        backgroundColor: 'rgba(102, 126, 234, 0.1)',
                        color: currentPage === 1 ? '#ccc' : '#667eea',
                        '&:hover': {
                            backgroundColor: currentPage === 1 ? 'rgba(102, 126, 234, 0.1)' : 'rgba(102, 126, 234, 0.2)',
                            transform: currentPage === 1 ? 'none' : 'scale(1.1)',
                        },
                        transition: 'all 0.3s ease'
                    }}
                >
                    <FirstPageIcon />
                </IconButton>
                
                <IconButton
                    onClick={handlePreviousPage}
                    disabled={currentPage === 1}
                    size="small"
                    sx={{
                        backgroundColor: 'rgba(102, 126, 234, 0.1)',
                        color: currentPage === 1 ? '#ccc' : '#667eea',
                        '&:hover': {
                            backgroundColor: currentPage === 1 ? 'rgba(102, 126, 234, 0.1)' : 'rgba(102, 126, 234, 0.2)',
                            transform: currentPage === 1 ? 'none' : 'scale(1.1)',
                        },
                        transition: 'all 0.3s ease'
                    }}
                >
                    <KeyboardArrowLeftIcon />
                </IconButton>
                
                {renderPageNumbers()}
                
                <IconButton
                    onClick={handleNextPage}
                    disabled={currentPage === totalPages}
                    size="small"
                    sx={{
                        backgroundColor: 'rgba(102, 126, 234, 0.1)',
                        color: currentPage === totalPages ? '#ccc' : '#667eea',
                        '&:hover': {
                            backgroundColor: currentPage === totalPages ? 'rgba(102, 126, 234, 0.1)' : 'rgba(102, 126, 234, 0.2)',
                            transform: currentPage === totalPages ? 'none' : 'scale(1.1)',
                        },
                        transition: 'all 0.3s ease'
                    }}
                >
                    <KeyboardArrowRightIcon />
                </IconButton>
                
                <IconButton
                    onClick={handleLastPage}
                    disabled={currentPage === totalPages}
                    size="small"
                    sx={{
                        backgroundColor: 'rgba(102, 126, 234, 0.1)',
                        color: currentPage === totalPages ? '#ccc' : '#667eea',
                        '&:hover': {
                            backgroundColor: currentPage === totalPages ? 'rgba(102, 126, 234, 0.1)' : 'rgba(102, 126, 234, 0.2)',
                            transform: currentPage === totalPages ? 'none' : 'scale(1.1)',
                        },
                        transition: 'all 0.3s ease'
                    }}
                >
                    <LastPageIcon />
                </IconButton>
            </Box>
        </Box>
    );
};

export default Pagination; 